import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const SESSION_KEY = "bb_intro_seen";

/**
 * Terminal-style intro: a black screen with the brand wordmark centered, shown
 * once per browser session on first load, then fades out to reveal the site.
 * Skippable (click / any key), respects reduced motion, and locks scroll while
 * visible. Decorative only — the page content is already in the DOM behind it.
 */
export function IntroSplash() {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return !sessionStorage.getItem(SESSION_KEY);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!show) return;
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* private mode — just show it this once */
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dismiss = () => setShow(false);
    // Hold the brand a full ~2s after it has finished animating in, then fade.
    const timer = window.setTimeout(dismiss, reduce ? 1500 : 2800);
    window.addEventListener("keydown", dismiss);
    window.addEventListener("pointerdown", dismiss);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", dismiss);
      window.removeEventListener("pointerdown", dismiss);
      document.body.style.overflow = prevOverflow;
    };
  }, [show, reduce]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 1.1, delay: reduce ? 0 : 0.6, ease: [0.16, 1, 0.3, 1] } }}
          aria-hidden="true"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_45%,rgba(125,255,166,0.08),transparent_70%)]" />
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10, filter: "blur(8px)" }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={
              reduce
                ? { opacity: 0 }
                : { opacity: 0, scale: 1.14, filter: "blur(16px)", transition: { duration: 1.5, ease: [0.22, 1, 0.36, 1] } }
            }
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative px-6 text-center"
          >
            <div className="text-[clamp(2.25rem,9vw,5.5rem)] font-bold leading-none tracking-tight text-white">
              BULL <span className="text-accent-500">&amp;</span> BEAR CO.
            </div>
            <motion.div
              initial={reduce ? { opacity: 1 } : { opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto mt-5 h-px w-44 origin-center bg-gradient-to-r from-transparent via-accent-500 to-transparent"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-4 text-[0.7rem] uppercase tracking-[0.45em] text-white/55 sm:text-xs"
            >
              Asset-Based Carrier
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
